import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  DialogContent,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  TextField,
} from "@mui/material";
import { Company } from "models/Company";
import { formatLongDate } from "utilities/formatDate";
import LogItem from "../components/LogItem";
import CommentItem from "../components/CommentItem";
import SendIcon from "@mui/icons-material/Send";
import LoadingDisplay from "components/LoadingDisplay";
import { useLogEntriesByDate } from "../../../hooks/useLogEntriesByDate";
import { useCreateCommentMutation } from "app/api/companies";
import EmptyMessage from "components/EmptyMessage";
import { useAuthContext } from "features/auth/AuthContext";
import { UserRole } from "features/auth/types/user";

type HistorySectionProps = { company: Company };

const HistorySection: React.FC<HistorySectionProps> = ({ company }) => {
  const { hasRole } = useAuthContext();
  const canEditCompanies = hasRole([UserRole.ADMIN, UserRole.USER_ADMIN]);

  const [commentText, setCommentText] = useState("");
  const [important, setImportant] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const [createComment] = useCreateCommentMutation();

  const { data: logEntriesByDate, isLoading } = useLogEntriesByDate(company.id);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [logEntriesByDate]);

  return (
    <DialogContent ref={contentRef} sx={{ p: 1 }}>
      {isLoading ? (
        <LoadingDisplay message="Hämtar händelser..." />
      ) : (
        <>
          <List sx={{ display: "grid" }} disablePadding>
            {!logEntriesByDate?.size && (
              <ListItem divider>
                <ListItemText primary={<EmptyMessage>Inga händelser registrerade</EmptyMessage>} />
              </ListItem>
            )}
            {Array.from(logEntriesByDate.entries()).map(([date, events]) => (
              <>
                <ListSubheader
                  disableSticky
                  sx={{
                    mt: 1,
                    px: 1,
                    color: "text.primary",
                    bgcolor: "transparent",
                    fontWeight: "bold",
                    lineHeight: "unset",
                    fontSize: "large",
                  }}
                >
                  {formatLongDate(new Date(date))}
                </ListSubheader>

                {events?.map((event) => {
                  if ("commentText" in event) {
                    return <CommentItem key={event.id} comment={event} />;
                  } else {
                    return <LogItem key={event.id} event={event} />;
                  }
                })}
              </>
            ))}
          </List>

          <Box
            component="form"
            hidden={!canEditCompanies}
            onSubmit={(e) => {
              e.preventDefault();

              createComment({
                companyId: company.id,
                commentText: commentText,
                important: important,
              })
                .unwrap()
                .then(() => {
                  setCommentText("");
                  setImportant(false);
                });
            }}
            sx={{
              maxWidth: "60ch",
              mx: "auto",
              px: 2,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <TextField
              label="Lägg till kommentar"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              variant="filled"
              fullWidth
              margin="normal"
              multiline
              rows={3}
              slotProps={{
                htmlInput: {
                  maxLength: 1000,
                },
              }}
              sx={{ flex: "1 1 100%" }}
            />

            <FormControlLabel
              label="Markera viktig kommentar"
              control={
                <Checkbox checked={important} onChange={(e) => setImportant(e.target.checked)} />
              }
            />

            <Button
              type="submit"
              variant="contained"
              disabled={!commentText}
              startIcon={<SendIcon />}
              sx={{ ml: "auto" }}
            >
              Skicka
            </Button>
          </Box>
        </>
      )}
    </DialogContent>
  );
};

export default HistorySection;
